import React, { useState, useMemo } from 'react';
import ActionMenu from '../ActionMenu/ActionMenu';
import './SharedTable.css';

/**
 * Zentrale SharedTable-Komponente (Gold Standard)
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of data objects
 * @param {Array} props.columns - Array of column definitions: { key, label, sortable, render, className }
 * @param {string} props.uniqueKeyField - Primary key field name (default: 'id')
 * @param {Function} props.onRowClick - Callback for row click: (item) => void
 * @param {Object} props.sortConfig - Optional external sort config: { key, direction: 'asc'|'desc' }
 * @param {Function} props.onSort - Optional callback for external sort: ({key, direction}) => void
 * @param {Array} props.selectedIds - Optional array of selected item IDs for bulk selection
 * @param {Function} props.onSelectionChange - Callback when selection changes: (ids) => void
 * @param {Array|Function} props.actions - Actions for ActionMenu (array or function returning array)
 * @param {boolean} props.isLoading - Whether the table is in loading state
 * @param {string} props.emptyMessage - Message to display when there are no items
 */
export default function SharedTable({
    data = [],
    columns = [],
    uniqueKeyField = 'id',
    onRowClick,
    sortConfig,
    onSort,
    selectedIds,
    onSelectionChange,
    actions,
    isLoading = false,
    emptyMessage = "Keine Einträge gefunden."
}) {
    // Pagination state
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);

    // Internal sort state (if not managed externally)
    const [internalSortConfig, setInternalSortConfig] = useState(null);

    const activeSortConfig = sortConfig !== undefined ? sortConfig : internalSortConfig;

    // Reset page if data changes significantly or page size changes
    useMemo(() => {
        setCurrentPage(1);
    }, [pageSize, data.length]);

    // Handle Sorting
    const handleSortClick = (column) => {
        if (!column.sortable) return;

        let direction = 'asc';
        if (activeSortConfig && activeSortConfig.key === column.key && activeSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        if (onSort) {
            onSort({ key: column.key, direction });
        } else {
            setInternalSortConfig({ key: column.key, direction });
        }
    };

    // Sort and paginate data
    const processedData = useMemo(() => {
        let sortedData = [...data];

        // Apply sorting
        if (activeSortConfig && activeSortConfig.key) {
            sortedData.sort((a, b) => {
                let aVal = a[activeSortConfig.key];
                let bVal = b[activeSortConfig.key];
                
                // Use empty string fallback for nullish
                if (aVal === null || aVal === undefined) aVal = '';
                if (bVal === null || bVal === undefined) bVal = '';

                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return activeSortConfig.direction === 'asc' 
                        ? aVal.localeCompare(bVal) 
                        : bVal.localeCompare(aVal);
                }

                if (aVal < bVal) return activeSortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return activeSortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        // Apply pagination
        if (pageSize !== 'all') {
            const startIndex = (currentPage - 1) * pageSize;
            return sortedData.slice(startIndex, startIndex + pageSize);
        }
        
        return sortedData;
    }, [data, activeSortConfig, pageSize, currentPage]);

    const totalPages = pageSize === 'all' ? 1 : Math.ceil(data.length / pageSize);

    // Handle Selection
    const hasSelection = selectedIds !== undefined && onSelectionChange;
    const allSelectedOnPage = hasSelection && processedData.length > 0 && processedData.every(item => selectedIds.includes(item[uniqueKeyField]));
    
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Select all current page items that aren't already selected
            const newSelections = processedData
                .map(item => item[uniqueKeyField])
                .filter(id => !selectedIds.includes(id));
            onSelectionChange([...selectedIds, ...newSelections]);
        } else {
            // Deselect all current page items
            const pageIds = processedData.map(item => item[uniqueKeyField]);
            onSelectionChange(selectedIds.filter(id => !pageIds.includes(id)));
        }
    };

    const handleSelectOne = (id, e) => {
        e.stopPropagation();
        if (selectedIds.includes(id)) {
            onSelectionChange(selectedIds.filter(i => i !== id));
        } else {
            onSelectionChange([...selectedIds, id]);
        }
    };

    // Render Actions
    const renderActions = (item) => {
        if (!actions) return null;
        const itemActions = typeof actions === 'function' ? actions(item) : actions;
        return <ActionMenu actions={itemActions} />;
    };

    return (
        <div className="shared-table-container">
            <div className="shared-table-wrapper">
                <table className="shared-table">
                    <thead>
                        <tr>
                            {hasSelection && (
                                <th className="shared-table-col-checkbox">
                                    <input 
                                        type="checkbox" 
                                        onChange={handleSelectAll}
                                        checked={allSelectedOnPage}
                                        disabled={isLoading || data.length === 0}
                                    />
                                </th>
                            )}
                            {columns.map((col) => (
                                <th 
                                    key={col.key}
                                    className={`shared-table-th ${col.sortable ? 'sortable' : ''} ${col.className || ''}`}
                                    onClick={() => handleSortClick(col)}
                                >
                                    <div className="shared-table-th-content">
                                        {col.label}
                                        {col.sortable && (
                                            <span className="sort-icon">
                                                {activeSortConfig?.key === col.key ? (
                                                    activeSortConfig.direction === 'asc' ? '↑' : '↓'
                                                ) : '↕'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                            {actions && <th className="shared-table-col-actions">Aktionen</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={columns.length + (hasSelection ? 1 : 0) + (actions ? 1 : 0)} className="shared-table-loading">
                                    <div className="skeleton-loader"></div>
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + (hasSelection ? 1 : 0) + (actions ? 1 : 0)} className="shared-table-empty">
                                    {emptyMessage}
                                </td>
                            </tr>
                        ) : (
                            processedData.map((item) => {
                                const id = item[uniqueKeyField];
                                const isSelected = hasSelection && selectedIds.includes(id);

                                return (
                                    <tr 
                                        key={id} 
                                        className={`${isSelected ? 'selected' : ''} ${onRowClick ? 'clickable' : ''}`}
                                        onClick={() => onRowClick && onRowClick(item)}
                                    >
                                        {hasSelection && (
                                            <td className="shared-table-col-checkbox" onClick={(e) => e.stopPropagation()}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={isSelected}
                                                    onChange={(e) => handleSelectOne(id, e)}
                                                />
                                            </td>
                                        )}
                                        {columns.map((col) => (
                                            <td key={col.key} className={col.className || ''}>
                                                {col.render ? col.render(item) : item[col.key]}
                                            </td>
                                        ))}
                                        {actions && (
                                            <td className="shared-table-col-actions" onClick={(e) => e.stopPropagation()}>
                                                {renderActions(item)}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {data.length > 0 && (
                <div className="shared-table-pagination">
                    <div className="pagination-info">
                        Zeige {pageSize === 'all' ? data.length : Math.min(data.length, (currentPage - 1) * pageSize + 1)} bis {pageSize === 'all' ? data.length : Math.min(data.length, currentPage * pageSize)} von {data.length} Einträgen
                    </div>
                    
                    <div className="pagination-controls">
                        <div className="page-size-selector">
                            <select 
                                value={pageSize} 
                                onChange={(e) => setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value="all">Alle</option>
                            </select>
                        </div>

                        {pageSize !== 'all' && (
                            <div className="page-buttons">
                                <button 
                                    className="page-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                >
                                    Zurück
                                </button>
                                <span className="page-current">Seite {currentPage} von {totalPages}</span>
                                <button 
                                    className="page-btn"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                >
                                    Vor
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
